import React, { useEffect, useMemo, useRef } from "react";
import { createColumnHelper, SortingState, Table } from "@tanstack/react-table";
import { PaneBody } from "@/components/pane/paneBody.tsx";
import { IColumn, IRow } from "@/domain/interfaces/sfTable.interface.ts";

import { applyFilters, cn } from "@/lib/utils.ts";
import { useGrid } from "@/hooks/useGrid.ts";
import { usePaneContext } from "@/components/pane/paneContext.ts";
import { HighlightedValueChange } from "@/components/shared/highlightedValueChange.tsx";
import { ESFTableType } from "@/domain/enums/dashboard.enum.ts";
import { Loader } from "@/components/shared/loader.tsx";
import { PaneSubHeader } from "@/components/pane/paneSubHeader.tsx";
import { useDashboard } from "@/lib/store";
import { useFilterData } from "@/hooks/useFilterData";
import { toast } from "../../ui/use-toast";
import { useAlertHistoryStore } from "@/lib/stores/useAlertHistoryStore";
import {
  ITopGainersPriceRow,
  useTopGainersPrice,
} from "@/hooks/useTopGainersPrice";
import { BINANCE_RESOLUTION_MAP } from "@/lib/datafeed/config";

const columnHelper = createColumnHelper<ITopGainersPriceRow>();

const columns = [
  columnHelper.accessor("symbol", {
    header: "Symbol",
    enableSorting: false,
  }),

  columnHelper.accessor("prc", {
    header: "Prc Chng",
    cell: (info) => {
      const value = info.getValue();
      return (
        <HighlightedValueChange
          value={value}
          highlightNewTextColor={false}
          highlightPosNeg={false}
          className={cn("font-bold")}
          decimal={2}
        />
      );
    },
    size: 110,
    minSize: 110,
  }),
];

const oldValues: IRow[] = [];
const highlightTimestamps: number[] = [];

function rowClassCb(
  row: ITopGainersPriceRow,
  idx: number,
  table: Table<IRow>,
  sortingState: SortingState,
) {
  if (idx > 4) {
    return "";
  }

  const meta = table.options.meta as { tableType: ESFTableType };
  const isHighlightable =
    meta?.tableType.includes(ESFTableType.unpinned) &&
    !meta?.tableType.includes(ESFTableType.chart);

  if (!isHighlightable) {
    return "";
  }

  const oldRow = oldValues[idx];
  const isPositionChanged = oldRow?.symbol !== row.symbol;
  const now = Date.now();
  const isSortedDesc = sortingState.some((sort) => sort.desc);
  const color = isSortedDesc ? "bg-green-600/20" : "bg-red-600/20";

  if (isPositionChanged) {
    oldValues[idx] = row;
    highlightTimestamps[idx] = now;
    return cn(color, "animate-pulse");
  } else if (
    highlightTimestamps[idx] &&
    now - highlightTimestamps[idx] < 1000
  ) {
    return cn(color, "animate-pulse");
  } else {
    return "";
  }
}

export function PriceScreenerPaneBody() {
  const nodeId = usePaneContext();
  const { getNodeConfig } = useGrid(nodeId);
  const config = getNodeConfig();

  const interval = BINANCE_RESOLUTION_MAP[config.interval];
  const { data: techDesk, isLoading } = useTopGainersPrice(interval);
  const filteredData = useFilterData<ITopGainersPriceRow>(techDesk || []);
  const { globalFilters, getGlobalFilterValues, blacklist } = useDashboard();
  const { addAlert } = useAlertHistoryStore();
  const prevSymbolsRef = useRef<Set<string>>(new Set());
  const preparedData = useMemo(() => {
    if (!filteredData || isLoading) {
      return [];
    }
    let preparedData = applyFilters(
      filteredData,
      globalFilters,
      getGlobalFilterValues(),
    );

    if (blacklist.length > 0) {
      blacklist.forEach((symbol) => {
        preparedData = preparedData.filter((item) => item.symbol !== symbol);
      });
    }

    if (config?.filters && config?.filterValues) {
      return applyFilters<ITopGainersPriceRow>(
        preparedData,
        config.filters,
        config.filterValues,
      );
    }

    return preparedData;
  }, [
    filteredData,
    isLoading,
    globalFilters,
    getGlobalFilterValues,
    blacklist,
    config.filters,
    config.filterValues,
  ]);

  useEffect(() => {
    if (config.isSound && preparedData.length > 0) {
      const currentSymbols = new Set(preparedData.map((item) => item.symbol));
      const prevSymbols = prevSymbolsRef.current;

      // Find new symbols that weren't in the previous data
      const newSymbols = [...currentSymbols].filter(
        (symbol) => !prevSymbols.has(symbol),
      );

      // If there are new symbols, get the one with highest value
      if (newSymbols.length > 0) {
        const newSymbolsData = preparedData.filter((item) =>
          newSymbols.includes(item.symbol),
        );

        if (newSymbolsData.length > 0) {
          const topNewSymbol = newSymbolsData[0].symbol;
          const msg = `Цена ${topNewSymbol.split("USDT")[0]}`;
          toast({
            title: "Price Screener Alert",
            description: `${topNewSymbol} alert ${newSymbolsData[0].prc}`,
          });
          addAlert({
            symbol: topNewSymbol,
            timestamp: Date.now(),
            message: `${topNewSymbol} alert ${newSymbolsData[0].prc}`,
            type: "price",
          });
          const utterThis = new SpeechSynthesisUtterance(msg);
          utterThis.voice = window.speechSynthesis
            .getVoices()
            .filter((voice) => voice.lang === "ru-RU")[0];
          utterThis.lang = "ru-RU";
          window.speechSynthesis.speak(utterThis);
        }
      }

      // Update previous symbols reference
      prevSymbolsRef.current = currentSymbols;
    }
  }, [preparedData, config.isSound, addAlert]);

  return isLoading ? (
    <Loader />
  ) : (
    <>
      <PaneSubHeader />
      <PaneBody
        className="!h-[calc(100%-24px)]"
        tableColumns={columns as IColumn[]}
        data={preparedData}
        rowClassCb={(...args) => rowClassCb(...args, config.sortingState)}
      />
    </>
  );
}
